/**
 * Porta de integração com o CRM (Dynamics 365) e mapeamento do documento para a Cotação.
 *
 * Este módulo é **puro**: não faz rede, não conhece o Power Automate e não importa SDK algum. Ele
 * define o contrato e a tradução. Quem executa a chamada HTTP é o adaptador em `services/api`, que
 * recebe uma implementação desta interface.
 *
 * A razão de a tradução morar aqui, e não no adaptador, é a decisão D3 aplicada ao CRM: o painel
 * precisa mostrar ao vendedor exatamente o que será enviado ao Dynamics, antes de publicar. Se o
 * mapeamento vivesse na Lambda, o painel teria de reimplementá-lo, e as duas versões divergiriam.
 *
 * O sentido inverso — CRM para Construtor — lista contas e oportunidades para seleção opcional no
 * formulário. Quando houver sincronização de Cotação, uma oportunidade escolhida continua sendo
 * validada por GUID antes do envio ao Dynamics.
 */

import {
  exigirOportunidade,
  type DocumentoProposta,
  type OportunidadeCrm,
  type SincronizacaoCrm,
} from './documento.ts';
import { arredondar } from './dinheiro.ts';
import { descricaoExibivel } from './csv-calculadora.ts';
import { NOME_COMERCIAL_SAFESTORAGE } from './configuracao.ts';

// ---------------------------------------------------------------------------
// Payload enviado ao CRM
// ---------------------------------------------------------------------------

/**
 * Linha da Cotação.
 *
 * O Dynamics distingue produto do catálogo de produto *write-in* (ad hoc) pelo campo
 * `isproductoverridden`. Serviços recorrentes da eMaster — SafePlace, assessment, treinamento — são
 * de catálogo e têm SKU. Recursos AWS são write-in: criar um produto para cada combinação de
 * serviço, região, família, tamanho e modelo de compra produziria um catálogo impossível de manter.
 */
export interface LinhaCotacao {
  /** Chave estável da linha, para idempotência e para reconciliar em revisões. */
  readonly idExternoLinha: string;
  /** SKU do catálogo. Nulo em linha write-in. */
  readonly sku: string | null;
  readonly descricao: string;
  /** Detalhamento técnico. Em recurso AWS, é a memória de cálculo. */
  readonly detalhe: string;
  readonly quantidade: number;
  readonly precoUnitario: number;
  readonly total: number;
  /** Natureza comercial usada para configurar o faturamento no Project Operations. */
  readonly naturezaCobranca: 'mensal' | 'pontual';
  /** `true` para linha write-in, que vira `isproductoverridden` no `quotedetail`. */
  readonly writeIn: boolean;
  /** Metadados do recurso AWS, para os campos customizados. Nulo em produto de catálogo. */
  readonly aws: {
    readonly servico: string;
    readonly regiao: string | null;
    readonly grupo: string;
  } | null;
}

/** Payload completo consumido pelo adaptador Dataverse Web API da Lambda. */
export interface PayloadCotacao {
  /** Número EM-AAAA-NNN gravado no campo nativo `quote.name`. */
  readonly idPropostaExterna: string;
  /** Revisão mantida no hash/auditoria do payload; não é exibida na descrição do Dynamics. */
  readonly revisao: number;
  /** GUID da oportunidade. Obrigatório: a Cotação é criada por GUID, nunca por nome. */
  readonly oportunidadeId: string;
  readonly oportunidadeNumero: string;
  readonly contaId: string | null;
  /** Trilha comercial: determina a lista de preço no servidor, nunca a oportunidade/navegador. */
  readonly trilha: 'CS' | 'PS';
  readonly nome: string;
  /** As listas CS e PS do contrato D17 são denominadas em USD. */
  readonly moeda: 'USD';
  readonly vigenciaInicio: string;
  readonly vigenciaFim: string;
  /** Promessa de valor principal exibida no início da descrição da Cotação. */
  readonly descricao: string;
  /** Texto livre opcional da proposta, normalizado sem HTML para a descrição da Cotação. */
  readonly detalhesAdicionais: string | null;
  readonly condicoesComerciais: string;
  readonly propostaUrl: string | null;
  readonly linhas: readonly LinhaCotacao[];
  readonly totais: {
    readonly servicosEmaster: number;
    readonly recursosAws: number;
    /** Custos adicionais faturados uma única vez (hoje, deploy/implantação). */
    readonly custosPontuais: number;
    /** Abatimento mensal do Crédito Especial AWS, aplicado no cabeçalho da Cotação. */
    readonly desconto: number;
    /** Parcela recorrente após o desconto mensal. */
    readonly mensalidade: number;
    /** Total da Cotação: mensalidade + custos pontuais. */
    readonly total: number;
  };
  /** Situação comercial no momento do envio. */
  readonly situacao: 'RASCUNHO' | 'PUBLICADA' | 'ACEITA';
  /** Hash do conteúdo, para detectar reenvio de payload idêntico. */
  readonly hashConteudo: string;
}

// ---------------------------------------------------------------------------
// Resultado e porta
// ---------------------------------------------------------------------------

/** Códigos de desfecho devolvidos pelo adaptador. Espelham a matriz de erros da integração. */
export type CodigoResultadoCrm =
  | 'SINCRONIZADA'
  | 'JA_SINCRONIZADA'
  | 'CONFLITO_DE_VERSAO'
  | 'OPORTUNIDADE_NAO_ENCONTRADA'
  | 'MOEDA_INCOMPATIVEL'
  | 'DIVERGENCIA_DE_TOTAL'
  | 'ERRO_TECNICO';

export interface ResultadoCrm {
  readonly codigo: CodigoResultadoCrm;
  readonly cotacaoId: string | null;
  readonly cotacaoNumero: string | null;
  readonly correlacaoId: string | null;
  readonly mensagem: string | null;
}

/**
 * Conta (empresa) do CRM, na forma enxuta que o seletor de cliente consome.
 *
 * É o suficiente para o vendedor reconhecer e escolher a empresa no formulário. CNPJ é opcional
 * porque nem toda conta o tem preenchido, mas quando existe ajuda a desambiguar homônimos na lista.
 */
export interface ContaCrm {
  readonly id: string;
  readonly nome: string;
  readonly cnpj: string | null;
}

/** Dados que o CRM devolve ao resolver um número de oportunidade. */
export interface OportunidadeImportada {
  readonly id: string;
  readonly numero: string;
  readonly nome: string;
  readonly contaId: string | null;
  readonly contaNome: string | null;
  readonly moeda: 'USD' | 'BRL' | null;
  readonly listaPrecoId: string | null;
  readonly proprietarioEmail: string | null;
}

/**
 * Forma enxuta de uma oportunidade para o seletor por cliente.
 *
 * Contém apenas o necessário para o vendedor reconhecer e selecionar uma oportunidade na lista.
 * Ao selecionar, o painel combina este resumo com a conta escolhida e grava o vínculo no documento.
 */
export interface OportunidadeResumo {
  readonly id: string;
  readonly numero: string;
  readonly nome: string;
}

/**
 * Porta do CRM.
 *
 * Duas operações, nos dois sentidos do fluxo. Manter as duas na mesma interface é deliberado: um
 * adaptador que só saiba enviar produziria a experiência que a decisão D13 rejeita, em que o vendedor
 * digita à mão o que o CRM já sabe.
 */
export interface CrmGateway {
  /**
   * Lista as contas (empresas) do CRM para o seletor de cliente.
   *
   * `busca` é opcional: vazio pede o começo da lista; preenchido filtra por parte do nome. O
   * `limite` protege contra puxar o catálogo inteiro de uma vez. A ordenação e o teto ficam a
   * cargo do adaptador, porque dependem da API do CRM.
   */
  listarContas(busca: string, limite: number): Promise<readonly ContaCrm[]>;
  /**
   * Lista as oportunidades ativas de uma conta, para o seletor por cliente.
   *
   * `contaId` é o GUID da conta (accountid). Devolve só as oportunidades em aberto (statecode ativo),
   * porque propor sobre uma oportunidade ganha/perdida não faz sentido no fluxo.
   */
  listarOportunidadesPorConta(contaId: string): Promise<readonly OportunidadeResumo[]>;
  /** Resolve o número informado pelo vendedor nos dados da oportunidade. */
  importarOportunidade(numero: string): Promise<OportunidadeImportada | null>;
  /** Cria ou atualiza a Cotação correspondente à proposta. Precisa ser idempotente. */
  sincronizarCotacao(payload: PayloadCotacao): Promise<ResultadoCrm>;
  /** Anexa o PDF da proposta formal à Cotação. */
  anexarDocumento(
    cotacaoId: string,
    nomeArquivo: string,
    pdfBase64: string,
  ): Promise<{ readonly url: string | null }>;
}

/**
 * Implementação nula.
 *
 * Existe para que a publicação funcione **antes** de o conector real existir, sem `if` espalhado pelo
 * handler. `importarOportunidade` devolve `null`, o que o formulário trata como "número não
 * resolvido"; `sincronizarCotacao` devolve `ERRO_TECNICO` com mensagem explícita, o que grava a
 * situação correta no documento em vez de fingir sucesso.
 *
 * Fingir sucesso seria pior que falhar: a proposta ficaria marcada como sincronizada sem Cotação
 * alguma no CRM, e ninguém descobriria até o fechamento do mês.
 */
export function crmGatewayNulo(): CrmGateway {
  return {
    listarContas: () => Promise.resolve([]),

    listarOportunidadesPorConta: () => Promise.resolve([]),

    importarOportunidade: () => Promise.resolve(null),

    sincronizarCotacao: () =>
      Promise.resolve({
        codigo: 'ERRO_TECNICO',
        cotacaoId: null,
        cotacaoNumero: null,
        correlacaoId: null,
        mensagem:
          'O conector do Dynamics 365 ainda não está configurado. A proposta foi publicada, mas nenhuma Cotação foi criada.',
      }),

    anexarDocumento: () => Promise.resolve({ url: null }),
  };
}

// ---------------------------------------------------------------------------
// Tradução do documento para o payload
// ---------------------------------------------------------------------------

/**
 * Hash estável do conteúdo comercial, usado para detectar reenvio idêntico.
 *
 * Implementação FNV-1a de 32 bits em hexadecimal. Não é criptográfico e não precisa ser: o objetivo é
 * comparar dois payloads, não resistir a colisão adversarial. Usar `crypto` traria dependência de
 * plataforma e violaria a fronteira do pacote (decisões D3 e D6).
 */
export function hashConteudo(entrada: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < entrada.length; i += 1) {
    h ^= entrada.charCodeAt(i);
    // Multiplicação por 16777619 em 32 bits, sem estourar o inteiro seguro do JavaScript.
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Frases fixas das condições comerciais, montadas a partir dos campos estruturados. */
export function condicoesComerciaisTexto(doc: DocumentoProposta): string {
  const moeda = doc.premissas.trilha === 'PS' ? 'Real' : 'US Dollar';
  const linhas = [
    `Prazo contratual de locação: ${String(doc.premissas.duracaoContratoMeses)} meses`,
    `Valores apresentados em ${moeda}`,
  ];

  const f = doc.formalizacao;
  if (f !== null) {
    if (f.condicoes.impostosInclusos) {
      linhas.push('Os valores incluem os impostos aplicáveis.');
    }
    linhas.push(
      `O faturamento ocorrerá até o dia ${String(f.condicoes.diaFaturamento)} do mês, com vencimento para o dia ${String(f.condicoes.diaVencimento)} ou próximo dia útil.`,
    );
    linhas.push(
      'Serviços cotados em outras moedas serão faturados conforme o câmbio fornecido pela AWS no dia do fechamento da fatura.',
    );
    linhas.push(`Forma de Pagamento: ${f.condicoes.formaPagamento}.`);
    linhas.push('Faturamento mensal efetuado conforme medição dos recursos consumidos no período.');

    const pct = f.condicoes.percentualProjetoImplantacao;
    if (pct !== null) {
      linhas.push(
        `O valor do Projeto de Implantação, cobrado pontualmente, é calculado considerando ${String(pct)}% da estimativa de faturamento acima.`,
      );
    }

    for (const extra of f.condicoes.clausulasAdicionais) {
      linhas.push(extra);
    }
  }

  return linhas.join('\n');
}

/** Converte as observações do editor em texto simples seguro para `quote.description`. */
function observacoesParaTextoCrm(valor: string | null): string | null {
  if (valor === null || valor.trim() === '') return null;

  const semBlocosExecutaveis = valor
    .replace(/<!--([\s\S]*?)-->/g, '')
    .replace(/<(script|style|iframe|object|embed|form|svg)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '');
  const comQuebras = semBlocosExecutaveis
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<li\b[^>]*>/gi, '- ')
    .replace(/<\/(p|div|li|h[1-6]|ul|ol|blockquote)\s*>/gi, '\n')
    .replace(/<\/?[a-z][^>]*>/gi, '');
  const texto = comQuebras
    .replace(/&#x([0-9a-f]+);/gi, (entidade, hexadecimal: string) => {
      const codigo = Number.parseInt(hexadecimal, 16);
      return Number.isSafeInteger(codigo) && codigo <= 0x10ffff
        ? String.fromCodePoint(codigo)
        : entidade;
    })
    .replace(/&#(\d+);/g, (entidade, decimal: string) => {
      const codigo = Number.parseInt(decimal, 10);
      return Number.isSafeInteger(codigo) && codigo <= 0x10ffff
        ? String.fromCodePoint(codigo)
        : entidade;
    })
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return texto === '' ? null : texto;
}

/**
 * Traduz o documento no payload da Cotação.
 *
 * Regras que esta função encapsula, e que existem para não deixar o CRM divergir da proposta:
 *
 * - o valor enviado é o **preço de venda**, já com margem, nunca o custo líquido do CSV;
 * - a moeda vem da trilha, não da oportunidade, porque a trilha é o que determinou o cálculo;
 * - recursos AWS entram consolidados por serviço, na mesma granularidade da tabela de investimento,
 *   para que o cliente veja no CRM o que viu na proposta.
 *
 * @throws {ErroPublicacao} quando a oportunidade não está resolvida.
 */
export function documentoParaPayloadCotacao(
  doc: DocumentoProposta,
  propostaUrl: string | null,
): PayloadCotacao {
  const oportunidade = exigirOportunidade(doc);
  const r = doc.resultados;
  const trilha = doc.premissas.trilha;
  // O contrato D17 usa as listas `Revenda AWS USA East1` e `Revenda AWS BR SP`, ambas em USD.
  // Portanto o valor enviado ao CRM é sempre a parcela USD do cálculo, inclusive na trilha PS.
  const moeda = 'USD' as const;

  const naMoeda = (v: { usd: number; brl: number | null }): number => v.usd;

  const linhas: LinhaCotacao[] = [];
  let totalAws = 0;
  let totalEmaster = 0;
  let totalPontual = 0;

  // --- Recursos AWS: write-in, um por item do CSV, com a margem já aplicada.
  const itens = doc.premissas.itensCusto;
  if (r.margemAplicada && itens !== null && itens.length > 0) {
    const fator = 1 - r.margemPercentual / 100;
    itens.forEach((item, indice) => {
      const vendaUsd = arredondar(item.custoMensalLiquidoUsd / fator);
      const valor = vendaUsd;

      // O `detalhe` vira o campo `description` da linha da Cotação. Leva a descrição do CSV junto
      // com a memória de cálculo para que o vendedor veja no Dynamics o mesmo texto da proposta.
      const contexto = descricaoExibivel(item.descricao);
      const detalhe = [contexto, item.resumoConfiguracao].filter((t) => t !== '').join(' — ');

      totalAws += valor;
      linhas.push({
        idExternoLinha: `AWS-${String(indice + 1).padStart(3, '0')}`,
        sku: null,
        descricao: item.servico,
        detalhe,
        quantidade: 1,
        precoUnitario: valor,
        total: valor,
        naturezaCobranca: 'mensal',
        writeIn: true,
        aws: { servico: item.servico, regiao: doc.premissas.regiaoAws, grupo: item.grupo },
      });
    });
  } else if (r.consumo.usd > 0) {
    const valor = naMoeda(r.consumo);
    totalAws += valor;
    linhas.push({
      idExternoLinha: 'AWS-001',
      sku: null,
      descricao: 'Infraestrutura AWS',
      detalhe: r.margemAplicada
        ? `Dimensionada com margem de gestão de ${String(r.margemPercentual)}%`
        : 'Conforme dimensionamento acordado',
      quantidade: 1,
      precoUnitario: valor,
      total: valor,
      naturezaCobranca: 'mensal',
      writeIn: true,
      aws: { servico: 'Consolidado', regiao: doc.premissas.regiaoAws, grupo: 'Infraestrutura' },
    });
  }

  // --- Plano de suporte: produto de catálogo, com SKU por faixa.
  if (r.custoSuporte.usd > 0) {
    const valor = naMoeda(r.custoSuporte);
    totalEmaster += valor;
    linhas.push({
      idExternoLinha: 'EM-SUPORTE',
      sku: `SAFEPLACE-${doc.premissas.planoSuporte.toUpperCase()}`,
      descricao: `Plano de suporte SafePlace ${doc.premissas.planoSuporte}`,
      detalhe: 'Sustentação, monitoramento e acompanhamento consultivo',
      quantidade: 1,
      precoUnitario: valor,
      total: valor,
      naturezaCobranca: 'mensal',
      writeIn: false,
      aws: null,
    });
  }

  // --- Licenças de backup: produto de catálogo.
  if (r.licencasBackup.usd > 0) {
    const valor = naMoeda(r.licencasBackup);
    totalEmaster += valor;
    linhas.push({
      idExternoLinha: 'EM-BACKUP',
      sku: 'EMASTER-BACKUP-ONPREM',
      descricao: 'eMaster Backup On-Premises',
      detalhe: 'Licenças mensais conforme quantidade contratada',
      quantidade: 1,
      precoUnitario: valor,
      total: valor,
      naturezaCobranca: 'mensal',
      writeIn: false,
      aws: null,
    });
  }

  /*
   * SafeStorage: produto de catálogo, com SKU por faixa.
   *
   * A quantidade continua 1 e o preço é o da assinatura, não o unitário por usuário. A faixa é um
   * degrau, não um múltiplo: 20 usuários custam a Constelação inteira, e lançar 20 × preço unitário
   * no CRM produziria uma Cotação com total que não bate com a proposta.
   *
   * A contagem de usuários vive em `detalhe`, que é onde o financeiro precisa dela para conferir o
   * degrau contratado.
   */
  if (r.plataformaSafeStorage.usd > 0 && doc.premissas.safestorage !== null) {
    const s = doc.premissas.safestorage;
    const valor = naMoeda(r.plataformaSafeStorage);
    totalEmaster += valor;
    linhas.push({
      idExternoLinha: 'EM-SAFESTORAGE',
      sku: `EMASTER-SAFESTORAGE-${s.plano.toUpperCase()}`,
      descricao: NOME_COMERCIAL_SAFESTORAGE[s.plano],
      detalhe: `Assinatura mensal da plataforma para ${String(s.usuarios)} ${
        s.usuarios === 1 ? 'usuário' : 'usuários'
      }. Armazenamento S3 faturado sob demanda, na linha de infraestrutura.`,
      quantidade: 1,
      precoUnitario: valor,
      total: valor,
      naturezaCobranca: 'mensal',
      writeIn: false,
      aws: null,
    });
  }

  // --- Deploy/implantação: custo adicional de cobrança única, fora da mensalidade.
  if (r.deployFaturado.usd > 0) {
    const valor = naMoeda(r.deployFaturado);
    totalPontual += valor;
    linhas.push({
      idExternoLinha: 'EM-IMPLANTACAO',
      sku: null,
      descricao: 'Projeto de implantação',
      detalhe:
        doc.premissas.deploy.modalidade === 'financiado'
          ? 'Saldo da implantação após a parcela coberta pelo Crédito Especial AWS; cobrança única.'
          : 'Implantação faturada como pagamento único, não recorrente.',
      quantidade: 1,
      precoUnitario: valor,
      total: valor,
      naturezaCobranca: 'pontual',
      writeIn: true,
      aws: null,
    });
  }

  const desconto = naMoeda(r.reducaoMensal);
  const mensalidade = arredondar(totalAws + totalEmaster - desconto);
  const total = arredondar(mensalidade + totalPontual);

  // A situação enviada ao CRM reflete o estágio comercial, não o estágio de edição: uma proposta
  // aceita permanece aceita mesmo que o documento seja reaberto para correção de texto.
  const foiAceita = doc.formalizacao !== null && doc.formalizacao.aceite.assinadoEm !== null;
  const situacao: PayloadCotacao['situacao'] = foiAceita
    ? 'ACEITA'
    : doc.situacao === 'publicada'
      ? 'PUBLICADA'
      : 'RASCUNHO';

  const descricao = doc.conteudo.promessaValor;
  const detalhesAdicionais = observacoesParaTextoCrm(doc.conteudo.observacoesHtml);
  const condicoes = condicoesComerciaisTexto(doc);

  /*
   * O hash identifica o payload efetivamente persistido no CRM — vínculos, lista (pela trilha),
   * moeda, textos, vigência, linhas completas, totais e situação. Assim, mudar cliente, SKU,
   * quantidade ou metadado AWS nunca pode ser confundido com reenvio idêntico só porque o total
   * permaneceu igual.
   */
  const base: Omit<PayloadCotacao, 'hashConteudo'> = {
    idPropostaExterna: doc.numero,
    revisao: doc.auditoria.versao,
    oportunidadeId: oportunidade.id,
    oportunidadeNumero: oportunidade.numero,
    contaId: oportunidade.contaId,
    trilha,
    nome: oportunidade.nome ?? `${doc.projeto} — ${doc.cliente}`,
    moeda,
    vigenciaInicio: doc.emitidaEm,
    vigenciaFim: doc.validaAte,
    descricao,
    detalhesAdicionais,
    condicoesComerciais: condicoes,
    propostaUrl,
    linhas,
    totais: {
      servicosEmaster: arredondar(totalEmaster),
      recursosAws: arredondar(totalAws),
      custosPontuais: arredondar(totalPontual),
      desconto: arredondar(desconto),
      mensalidade,
      total,
    },
    situacao,
  };

  return { ...base, hashConteudo: hashConteudo(JSON.stringify(base)) };
}

/**
 * Converte os dados importados do CRM no bloco de oportunidade do documento.
 *
 * `importadoEm` recebe o instante da importação, o que distingue dado vindo do CRM de dado digitado
 * — informação necessária para decidir se vale reconsultar.
 */
export function oportunidadeDeImportacao(
  importada: OportunidadeImportada,
  agora: string,
): OportunidadeCrm {
  return {
    numero: importada.numero,
    id: importada.id,
    nome: importada.nome,
    contaId: importada.contaId,
    contaNome: importada.contaNome,
    moeda: importada.moeda,
    listaPrecoId: importada.listaPrecoId,
    proprietarioEmail: importada.proprietarioEmail,
    importadoEm: agora,
  };
}

/** Traduz o resultado do CRM no bloco de sincronização do documento. */
export function sincronizacaoDeResultado(r: ResultadoCrm, agora: string): SincronizacaoCrm {
  const bemSucedido = r.codigo === 'SINCRONIZADA' || r.codigo === 'JA_SINCRONIZADA';

  const situacao =
    r.codigo === 'SINCRONIZADA' || r.codigo === 'JA_SINCRONIZADA'
      ? 'sincronizada'
      : r.codigo === 'ERRO_TECNICO'
        ? 'erro_tecnico'
        : 'erro_validacao';

  return {
    situacao,
    cotacaoId: r.cotacaoId,
    cotacaoNumero: r.cotacaoNumero,
    correlacaoId: r.correlacaoId,
    sincronizadoEm: bemSucedido ? agora : null,
    erro: bemSucedido ? null : r.mensagem,
  };
}
